import React, { useEffect, useId, useRef } from "react";
import { SmallCaps } from "./primitives";
import { useI18n } from "../../lib/i18n";
import { lockModalSurface, releaseModalSurface } from "../../lib/modalSurface";

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Scroll lock is reference-counted rather than snapshotted per modal. In
 * development React mounts effects twice, so a naive snapshot captures the
 * "hidden" its own first run just wrote and restores that on close, leaving the
 * page permanently unscrollable. Counting also keeps stacked modals honest.
 */
let scrollLocks = 0;
let scrollLockOriginal = "";

function lockScroll() {
  if (scrollLocks === 0) {
    scrollLockOriginal = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  scrollLocks += 1;
  // A modal covers the page, so the app's own tab bar must not sit on top of it.
  lockModalSurface();
}

function releaseScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = scrollLockOriginal;
  releaseModalSurface();
}

/** Full-page atelier modal — ivory sheet slides in from the right on desktop,
 *  full-screen on mobile.
 *
 *  Every atelier overlay (try-on, concierge, appointment, size guide, avatar)
 *  is built on this shell, so the dialog semantics live here rather than being
 *  repeated — and previously missing — in each one. It announces itself as a
 *  dialog, keeps Tab inside while open, stops the page behind it from
 *  scrolling, and hands focus back to whatever opened it.
 */
export function AtelierModal({ open, onClose, title, subtitle, children, testid = "atelier-modal", width = "560px" }) {
  const { t } = useI18n();
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    lockScroll();

    // Move focus into the sheet so a keyboard or screen-reader user is not
    // left behind on the page underneath.
    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    (first || panel)?.focus?.();

    const onKeyDown = (event) => {
      if (event.key === "Escape") { onClose?.(); return; }
      if (event.key !== "Tab" || !panel) return;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      releaseScroll();
      // Only return focus if the trigger is still on the page — after a route
      // change it may be gone, and focusing a detached node does nothing.
      const trigger = restoreFocusRef.current;
      if (trigger && document.contains(trigger)) trigger.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid={testid}>
      <div
        className="absolute inset-0 bg-graphite/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        data-testid={`${testid}-backdrop`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-ivory text-graphite overflow-y-auto shadow-xl animate-slide-in-right w-full focus:outline-none"
        style={{ maxWidth: width }}
      >
        <div className="px-6 sm:px-8 lg:px-12 py-10">
          <div className="flex items-start justify-between gap-4 mb-8">
            <div id={titleId}>
              <SmallCaps>{title}</SmallCaps>
              {subtitle && <div className="font-serif italic text-2xl mt-2 leading-snug">{subtitle}</div>}
            </div>
            {/* min-h-11 / -mr-2 px-2: the label alone was a 17px-tall tap target,
                well under the 44px touch minimum, on the one control every mobile
                user needs to reach. */}
            <button
              onClick={onClose}
              data-testid={`${testid}-close`}
              aria-label={t("common.close", "Close")}
              className="atelier-link -mr-2 flex min-h-11 shrink-0 items-center px-2 text-[11px] uppercase tracking-widest text-graphite/70"
            >
              {t("common.close", "Close")} ×
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
